import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import {
  AccountNeedingReconnection,
  ReconnectionReason,
  dismissForOneWeek,
} from "@/hooks/useAccountsNeedingReconnection";
import { getAccountDisplayName } from "@/entities/accounts/libs/get-accounts-name";
import { getAccountPlatformType } from "@/entities/accounts/libs/account-platform";
import { platformAccountNameMap, AccountType } from "@/shared/types/accounts";

interface AccountReconnectionDialogProps {
  open: boolean;
  accountsNeedingReconnection: AccountNeedingReconnection[];
  onDismiss: () => void;
}

/**
 * Simple avatar component with image error handling fallback
 */
function AccountAvatarSimple({ account }: { account: AccountType }) {
  const [imageError, setImageError] = useState(false);
  const showFallback = !account.profilePictureUrl || imageError;

  if (showFallback) {
    return (
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white"
        style={{
          backgroundColor: account.color || "#6366f1",
        }}
      >
        {(account.login || "?").charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={account.profilePictureUrl}
      alt=""
      className="h-8 w-8 shrink-0 rounded-full object-cover"
      onError={() => setImageError(true)}
    />
  );
}

function getReasonText(reason: ReconnectionReason): string {
  switch (reason) {
    case "failed":
      return "Connection failed";
    case "paused":
      return "Account paused";
    case "promptApiReconnect":
      return "API token expired";
    case "linkedinScopes":
      return "Missing LinkedIn permissions";
    case "tiktokScopes":
      return "Missing TikTok permissions";
    case "missingScopes":
      return "Missing permissions for some features";
    default:
      return "Needs attention";
  }
}

function getPlatformDisplayName(platformType: string): string {
  const platformName =
    platformAccountNameMap[platformType as keyof typeof platformAccountNameMap];
  if (!platformName) return platformType;

  if (platformName === "googlemybusiness") return "Google Business Profile";

  return platformName.charAt(0).toUpperCase() + platformName.slice(1);
}

/**
 * Dialog shown on app startup when accounts need reconnection.
 * Can be dismissed for 1 week.
 */
export function AccountReconnectionDialog({
  open,
  accountsNeedingReconnection,
  onDismiss,
}: AccountReconnectionDialogProps) {
  const navigate = useNavigate();
  const [isDismissing, setIsDismissing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleGoToAccounts = useCallback(() => {
    onDismiss();
    navigate("/dashboard/accounts");
  }, [navigate, onDismiss]);

  const handleDismissForWeek = useCallback(() => {
    setIsDismissing(true);
    dismissForOneWeek();
    onDismiss();
  }, [onDismiss]);

  const INITIAL_VISIBLE_COUNT = 5;
  const hasMoreAccounts =
    accountsNeedingReconnection.length > INITIAL_VISIBLE_COUNT;
  const hiddenCount =
    accountsNeedingReconnection.length - INITIAL_VISIBLE_COUNT;
  const visibleAccounts = isExpanded
    ? accountsNeedingReconnection
    : accountsNeedingReconnection.slice(0, INITIAL_VISIBLE_COUNT);

  const totalAccounts = accountsNeedingReconnection.length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDismiss()}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {totalAccounts === 1
              ? "1 Account Needs Attention"
              : `${totalAccounts} Accounts Need Attention`}
          </DialogTitle>
          <DialogDescription>
            Some of your social accounts need to be reconnected to continue
            posting and accessing all features.
          </DialogDescription>
        </DialogHeader>

        {/* Account list - compact design with expand/collapse */}
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 overflow-hidden mt-3">
          {/* Only constrain height and enable scroll when there are many accounts (10+) */}
          <div
            className={`space-y-px ${
              isExpanded && accountsNeedingReconnection.length >= 10
                ? "max-h-96 overflow-y-auto"
                : ""
            }`}
          >
            {visibleAccounts.map(({ account, reasons }) => {
              const platformType = getAccountPlatformType(account);
              return (
                <div
                  key={account._id}
                  className="flex items-center gap-3 bg-white px-3 py-2.5 transition-colors hover:bg-neutral-50"
                >
                  <AccountAvatarSimple account={account} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-neutral-900">
                        {getAccountDisplayName(account)}
                      </span>
                      <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-500">
                        {getPlatformDisplayName(platformType)}
                      </span>
                    </div>
                    <p className="truncate text-xs text-amber-600/80">
                      {reasons.map(getReasonText).join(" · ")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expand/collapse button */}
          {hasMoreAccounts && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex w-full items-center justify-center gap-1.5 border-t border-neutral-200 bg-neutral-50 px-3 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-neutral-100 hover:text-blue-700 cursor-pointer"
            >
              {isExpanded ? (
                <>
                  Show less
                  <i className="fa-regular fa-chevron-up text-xs" />
                </>
              ) : (
                <>
                  and {hiddenCount} more{" "}
                  {hiddenCount === 1 ? "account" : "accounts"}
                  <i className="fa-regular fa-chevron-down text-xs" />
                </>
              )}
            </button>
          )}
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="blurple-secondary"
            onClick={handleDismissForWeek}
            disabled={isDismissing}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Remind me later
          </Button>
          <Button
            variant="blue-primary"
            onClick={handleGoToAccounts}
            className="w-full sm:w-auto order-1 sm:order-2"
          >
            Go to Accounts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
